import { Component, OnInit, Inject, ViewChild, ElementRef, Optional, Input } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { TradingService } from '../../../services/common/trading-service.service';
import { TRADING_SETTINGS_ASSET_TYPE, TRADING_SETTINGS_ROLE, TRADING_PLATFORM, CONTAINER_TYPE, ALERT_MESSAGE_LEVEL } from '../../../utils/trading-constants';
import { AccountType, OrderType, OrderDuration, TradeType, UserType, Account } from '../../../models/common/trading-common.model';
import { FormGroup, FormBuilder, AbstractControl } from '@angular/forms';
import { createTradingSettingsForm, GetUserType, isAveragePriceAccount } from '../../../utils/trading-common';
import { TradingEntitlements } from '../../../models/common/trading-entitlements';
import { BuyAtNavIndicator } from '../../../models/mutualfund/trading-mutualfund.model';
import { AlertMessageDialogComponent } from '../alert-message-dialog/alert-message-dialog.component';

@Component({
  selector: 'order-settings',
  templateUrl: './order-settings.component.html',
  styleUrls: ['./order-settings.component.css']
})
export class OrderSettingsComponent implements OnInit {
  @Input() containerType: CONTAINER_TYPE;
  @Input() inputAccount: Account;
  @ViewChild('saveButton', { read: ElementRef }) saveButton: ElementRef;

  isBdUser: boolean;
  isRiaUser: boolean;
  isDualUser: boolean;
  isAdmin: boolean;
  isAdminView: boolean = true;
  isEditing = false;
  isEditButtonDisabledForUser: boolean;
  showErrorMessage = false;
  showSavedConfirmation = false;
  isAvgPrcAccount: boolean = false;
  showDiscretion: boolean=false;
  showSolicited: boolean=false;
  isClearDefaultSetting: boolean = false;
  isUserDefaultEmpty: boolean = false;
  isTradingTypeChange: boolean = false;
  securityTypesList = [TradeType.EQUITY, TradeType.MUTUAL_FUND];
  ibdDefaultValues: Map<string, string> = new Map<string, string>();
  userIBDMemoValue:string;
  securityType: string;

  ip: string;
  ipName: string;
  lastUpdateTime: Date;

  role: TRADING_SETTINGS_ROLE;
  previousRole: TRADING_SETTINGS_ROLE;
  adminRole: string = TRADING_SETTINGS_ROLE.ADMIN;
  userRole: string = TRADING_SETTINGS_ROLE.USER;
  tradingEntitlements: TradingEntitlements;
  platform: string = TRADING_PLATFORM.PROFESSIONAL;
  settingsResp: any;
  settingsIbdResp: any;
  tradingSettingsForm: FormGroup;
  account: any;
  showClose: boolean = true;
  displayColumns: string = 'col-xs-6';
  orderSettingsSaveInProgress: boolean = false;
  activeTheme: string;
  // AccountType value mapping
  ACTYP_VALUE_MAPPING = {
    '1': AccountType.CASH,
    '2': AccountType.MARGIN
  };
  accountTypeList = ['1', '2'];

  // OrderType value mapping
  ORDTY_VALUE_MAPPING = {
    '1': OrderType.MARKET,
    '2': OrderType.LIMIT,
    '3': OrderType.STOP,
    '3SL': OrderType.STOP_LIMIT,
    'C': OrderType.EXECUTE_AT_CLOSE,
    'O': OrderType.OR_BETTER,
    'W': OrderType.WOW,
    '2LOC': OrderType.LIMIT_ON_CLOSE,
    '6_TRAILING_STOP_DOLLAR': OrderType.TRAILING_STOP_DOLLAR,
    '6_TRAILING_STOP_PCT': OrderType.TRAILING_STOP_PCT,
    '6_TRAILING_STOP_LIMIT_DOLLAR': OrderType.TRAILING_STOP_LIMIT_DOLLAR,
    '6_TRAILING_STOP_LIMIT_PCT': OrderType.TRAILING_STOP_LIMIT_PCT
  };
  // Add Limit on close for future Implementation 'L'
  orderTypeList = ['1', '2', '3', '3SL', 'C', '2LOC'];

  orderTypeOptionsList = ['1', '2', '3', '3SL'];

  yesNoList = ['Y', 'N'];

  // BuyAtNav value mapping
  BUY_AT_NAV_VALUE_MAPPING = {
    'N': BuyAtNavIndicator.NO,
    '0': BuyAtNavIndicator.YES_OTHERS,
    '1': BuyAtNavIndicator.YES_NAV_TRANSFER,
    '2': BuyAtNavIndicator.YES_REPURCHASES,
    '3': BuyAtNavIndicator.YES_EMPLOYEE_REP,
    '4': BuyAtNavIndicator.YES_ERROR_CORRECTION,
    '5': BuyAtNavIndicator.YES_WRAP_FEE_ACCT,
    '6': BuyAtNavIndicator.YES_NAV_COMMISSIONABLE,
    '7': BuyAtNavIndicator.YES_COMM_DEFINED_CONTRIBUTION_PLAN,
    '8': BuyAtNavIndicator.YES_NON_COMM_DEFINED_CONTRIBUTION_PLAN,
    'A': BuyAtNavIndicator.YES_DIVIDEND_REINVESTMENT_TRADE,
    'B': BuyAtNavIndicator.YES_TRUST_ACCT,
    'E': BuyAtNavIndicator.YES_SELF_DIRECTED
  };
  buyAtNavList = ['N', '0', '1', '2', '3', '4', '5', '6', '7', '8', 'A', 'B', 'E'];

  // trailingBasedOn value mapping
  TBASE_VALUE_MAPPING = {
    'LTP': 'selectOptions.trailingBasedOn.B',
    'LQP': 'selectOptions.trailingBasedOn.A'
  };
  trailingBasedOnList = ['LTP', 'LQP'];

  // duration value mapping
  DURTN_VALUE_MAPPING = {
    '1': OrderDuration.DAY,
    '2': OrderDuration.GTC,
    '3': OrderDuration.OPG,
    '4': OrderDuration.IOC,
    '5': OrderDuration.FOK,
    '7': OrderDuration.GTD,
    '8': OrderDuration.GTM
  };
  durationList = ['1', '2', '7', '8', '5', '3', '4'];

  // useAveragePrice value mapping
  TUAPR_VALUE_MAPPING = {
    'Y': 'labels.orderSettings.Y',
    'N': 'labels.orderSettings.N'
  };

  // solicited value mapping
  SOLIC_VALUE_MAPPING = {
    'Y': 'selectOptions.solicitedSelect.true',
    'N': 'selectOptions.solicitedSelect.false'
  };

  // discretion value mapping
  DISCR_VALUE_MAPPING = {
    'Y': 'selectOptions.discretionSelect.Y',
    'N': 'selectOptions.discretionSelect.N'
  };

  // sourceOfInput value mapping
  INPUT_VALUE_MAPPING = {
    '3': 'selectOptions.sourceOfInputSelect.3',
    'I': 'selectOptions.sourceOfInputSelect.I',
    'J': 'selectOptions.sourceOfInputSelect.J',
    'K': 'selectOptions.sourceOfInputSelect.K',
    'Z': 'selectOptions.sourceOfInputSelect.Z'
  };
  sourceOfInputList = ['3', 'I', 'J', 'K', 'Z'];

  // commissionType value mapping
  COMTY_VALUE_MAPPING = {
    'RTE': 'selectOptions.commTypeSelect.RTE',
    'MLT': 'selectOptions.commTypeSelect.MLT',
    'COM': 'selectOptions.commTypeSelect.COM',
    'MUD': 'selectOptions.commTypeSelect.MUD',
    'MUD-AUTO': 'selectOptions.commTypeSelect.MUD_AUTO',
    'PFM': 'selectOptions.commTypeSelect.PFM'
  };
  commissionTypeList = ['RTE', 'MLT', 'COM', 'MUD', 'MUD-AUTO', 'PFM'];

  commissionTypeOptionList = ['RTE', 'MLT', 'COM', 'PFM'];

  // deskRouteCode value mapping
  DESKCODE_VALUE_MAPPING = {
    'A': 'A',
    'AR': 'AR',
    'B': 'B',
    'C': 'C',
    'D': 'D',
    'IS': 'IS',
    'IN': 'IN',
    'O': 'O',
    'PF': 'PF',
    'PT': 'PT',
    'PR': 'PR',
    'S': 'S',
    'T': 'T',
    'TR': 'TR',
    'SW': 'SW',
    'EC': 'EC',
    'CR': 'CR'
  };
  deskRouteCodeList = ['A', 'AR', 'B', 'C', 'D', 'IS', 'IN', 'O', 'PF', 'PT', 'PR', 'S', 'T', 'TR', 'SW', 'EC', 'CR'];

  REINVEST_VALUE_MAPPING = {
    'REINVEST': 'Reinvest Dividends & Capital Gains',
    'DIVIDEND': 'Reinvest Dividends Only',
    'CAPITAL': 'Reinvest Capital Gains Only',
    'CASH': 'Cash'
  };
  reinvestList = ['REINVEST', 'DIVIDEND', 'CAPITAL', 'CASH'];

  constructor(
    private tradingService: TradingService,
    private fb: FormBuilder,
    public dialog: MatDialog,
    @Optional() public dialogRef: MatDialogRef<OrderSettingsComponent>,
    @Optional() @Inject('AppSettings') appSettings: any,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    if (appSettings) {
      this.platform = appSettings.platform;
      
    }
  }

  ngOnInit() {
    this.activeTheme = this.tradingService.getActiveThemeFromCurrentContainer();
    this.tradingEntitlements = this.tradingService.tradingEntitlements;
    this.isAdmin = !!this.tradingEntitlements.isSettingsAdminViewEnabled;
    this.role = this.isAdminView ? TRADING_SETTINGS_ROLE.ADMIN : TRADING_SETTINGS_ROLE.USER;
    //The concat resul list needs to be assigned to orderTypeList, but the trailing stop options don't get saved, 
    //for that reason we will keep it like this until this issue is resolved in the DB or the requirements are clarified.
    if (this.tradingEntitlements.isTrailingStopEnabled) {
      this.orderTypeList = this.orderTypeList.concat(['6_TRAILING_STOP_DOLLAR', '6_TRAILING_STOP_PCT', '6_TRAILING_STOP_LIMIT_DOLLAR', '6_TRAILING_STOP_LIMIT_PCT']);
    }

    if (this.data) {
      this.account = this.data.account;
    } else if (this.inputAccount) {
      this.account = this.inputAccount;
      this.showClose = false;
    }
    
    if (this.tradingService.tradingEntitlements && this.tradingService.tradingEntitlements.isOptionTradingSupported) {
      this.securityTypesList = [TradeType.EQUITY, TradeType.MUTUAL_FUND, TradeType.OPTION];
    }
    const userType = GetUserType(this.account, this.tradingEntitlements);
    this.isBdUser = (userType === UserType.BD || userType === UserType.DUAL);
    this.isRiaUser = (userType === UserType.RIA || userType === UserType.DUAL);
    this.isDualUser = (userType === UserType.DUAL);

    this.isAvgPrcAccount = isAveragePriceAccount(this.account.acctNum);
    this.setSecurityType();
    this.tradingSettingsForm = createTradingSettingsForm(this.fb);
    this.fetchTradingSettings();
    this.ip = this.account.ibdId || '';
    this.ipName = ''; 
    this.getDisplayColumns();
    if(this.isRiaUser && !!this.account && this.account.ibdId !== '3CR' && this.account.ibdId !== 'UA1')
    {
      this.showDiscretion = true;
    }
    if (this.isRiaUser && !!this.account && this.account.ibdId === 'C01' && this.account.acctProdtProfileCd === 'PMAS') {
      this.showSolicited = true;
    }
  }

  private setSecurityType(): void {
    // Before fetching settings for the first time, see what our current wrapper is and
    // initialize securityType based on that
    let currContainer = this.tradingService.currentContainer;
    if (!currContainer && this.containerType) {
      currContainer = this.containerType;
    } else if (!this.containerType && currContainer) {
      this.containerType = currContainer;
    }

    if (currContainer === CONTAINER_TYPE.Equity || currContainer === CONTAINER_TYPE.Extended) {
      this.securityType = TradeType.EQUITY;
    } else if (currContainer === CONTAINER_TYPE.MutualFund || currContainer === CONTAINER_TYPE.Plan529) {
      this.securityType = TradeType.MUTUAL_FUND;
    } else if (currContainer === CONTAINER_TYPE.Option) {
      this.securityType = TradeType.OPTION;
    }
  }

  getDisplayColumns() {
    if (!this.isAdminView) {
      this.displayColumns = 'col-xs-4';
    } else if (this.isAdminView) {
      this.displayColumns = 'col-xs-3';
    }
  }

  tradingTypeChanged() {
    if (this.role === TRADING_SETTINGS_ROLE.USER) {
      this.isTradingTypeChange = true;
      this.role = TRADING_SETTINGS_ROLE.ADMIN;
      this.fetchTradingSettings();
    } else {
      this.isTradingTypeChange = false;
      this.fetchTradingSettings();
    }
  }

  fetchTradingSettings() {
    this.showSavedConfirmation = false;
    this.showErrorMessage = false;
    const ibdId = this.account.ibdId;
    let tradingType = TRADING_SETTINGS_ASSET_TYPE.EQT;
    if (this.securityType === TradeType.MUTUAL_FUND) {
      tradingType = TRADING_SETTINGS_ASSET_TYPE.MFT;
    }
    if (this.securityType === TradeType.OPTION) {
      tradingType = TRADING_SETTINGS_ASSET_TYPE.OPT;
    }

    this.tradingService.getTradingSettings(this.platform, ibdId, tradingType, this.role).subscribe(
      resp => this.handleSuccess(resp),
      err => this.handleError(err)
    );
  }

  private handleSuccess(resp) {
    if (resp && resp.errorCode === 0 && !!resp.response) {
      if (!!resp.response.tradeDefaultSettingList && !this.processRespond(resp)) {
        return;
      }
      if (!!resp.response.tradeDefaultSettingInfo?.settingUpdatedDate) {
        this.lastUpdateTime = resp.response.tradeDefaultSettingInfo.settingUpdatedDate;
      }
    } else {
      this.showErrorMessage = true;
    }
    if (this.isTradingTypeChange) {
      this.role = TRADING_SETTINGS_ROLE.USER;
      this.isTradingTypeChange = false;
      this.fetchTradingSettings();
    }
  }

  private processRespond(resp): boolean {
    this.isEditing = false;
    this.settingsResp = resp.response.tradeDefaultSettingList;
    this.isEditButtonDisabledForUser = false;
    this.setFormInitialValues(resp.response.tradeDefaultSettingList);
    if (this.role === TRADING_SETTINGS_ROLE.ADMIN) {
      this.settingsIbdResp = resp.response.tradeDefaultSettingList;
      //if there is any null for AllowToEditInd, it needs to be replaced and saved as Yes, by default
      //this is to allow the order entry to load the values
      if(this.adminLoadReplaceNullAllowToEditInd(resp.response.tradeDefaultSettingList)){
        return false;
      }
    }
    if (this.role === TRADING_SETTINGS_ROLE.USER && !!this.settingsResp && this.settingsResp.length === 0) {
      // set the default values from Admin View
      this.isUserDefaultEmpty = true;
    } else {
      this.isUserDefaultEmpty = false;
    }
    if (this.role === null) {
      // notify trading entry wrapper to update trading setting defaults
      this.tradingService.tradingDefaultsNotification(resp.response.tradeDefaultSettingList);
    }
    return true;
  }

  private handleError(err) {
    this.showErrorMessage = true;
    if (this.isTradingTypeChange) {
      this.role = TRADING_SETTINGS_ROLE.USER;
      this.isTradingTypeChange = false;
      this.fetchTradingSettings();
    }
  }

  private setFormValue(settingCode:string, item:any, itemDisplay:AbstractControl, itemDisplayEditable:AbstractControl) :void {
    if (item.settingCode === settingCode) {
      itemDisplay.setValue(item.settingValue);
      itemDisplayEditable.setValue(item.allowToEditInd);
    }
  }

  private setFormValueIBDMO(item:any, itemDisplay:AbstractControl, itemDisplayEditable:AbstractControl) :void {
  if (item.settingCode === 'IBDMO') {
    let memo = (item.settingValue || '');
    if (this.role === TRADING_SETTINGS_ROLE.USER) {
      this.userIBDMemoValue = item.settingValue;
    }
    if (memo.lastIndexOf('ADDL:', 0) === 0) {   // for equity
      memo = memo.substring(5, memo.length);
      itemDisplay.setValue(memo);
      itemDisplayEditable.setValue(item.allowToEditInd);
    } else if (memo.lastIndexOf('DIM:', 0) === 0) {    // for option
      memo = memo.substring(4, memo.length);
      itemDisplay.setValue(memo);
      itemDisplayEditable.setValue(item.allowToEditInd);
    }
  }
  }
  
  private setFormInitialValues(settings) {
    this.tradingSettingsForm.reset();
    this.isClearDefaultSetting = false;
    let reinvestCapital: string;
    let reinvestDiv: string;
    let reinvestCapitalEditable: string;
    let reinvestDivEditable: string;
    this.userIBDMemoValue = "";
    if (this.role === TRADING_SETTINGS_ROLE.ADMIN) {
      this.ibdDefaultValues = new Map<string, string>();
    }

    (settings || []).forEach(item => {
      const settingCode = item.settingCode;
      if (this.role === TRADING_SETTINGS_ROLE.ADMIN) {
        this.ibdDefaultValues.set(settingCode, item.settingValue);
      }
      // Additional Order Information block
      this.setFormValue('ADINF', item, this.addOrdInfAlwaysDisplay(), this.addOrdInfAlwaysDisplayEditable());
      
      // Commission  & IP Override
      this.setFormValue('COMMI', item, this.commissionAlwaysDisplay(), this.commissionAlwaysDisplayEditable());
      
      // Regulatory Information
      this.setFormValue('REGUL', item, this.regInfAlwaysDisplay(), this.regInfAlwaysDisplayEditable());
      
      // AccountType
      this.setFormValue('ACTYP', item, this.accountType(), this.accountTypeEditable());
      
      // OrderType
      this.setFormValue('ORDTY', item, this.orderType(), this.orderTypeEditable());
      
      // trailingBasedOn
      this.setFormValue('TBASE', item, this.trailingBasedOn(), this.trailingBasedOnEditable());
      
      // duration
      this.setFormValue('DURTN', item, this.duration(), this.durationEditable());
      
      // useAveragePrice
      this.setFormValue('TUAPR', item, this.useAveragePrice(), this.useAveragePriceEditable());
      
      // solicited
      this.setFormValue('SOLIC', item, this.solicited(), this.solicitedEditable());
      
      // discretion
      this.setFormValue('DISCR', item, this.discretion(), this.discretionEditable());
      
      // sourceOfInput
      this.setFormValue('INPUT', item, this.sourceOfInput(), this.sourceOfInputEditable());
      
      // commissionType
      this.setFormValue('COMTY', item, this.commissionType(), this.commissionTypeEditable());
      
      // ibdMemo
      this.setFormValueIBDMO(item, this.ibdMemo(), this.ibdMemoEditable());
      
      // deskRoute1Code
      this.setFormValue('DESK1', item, this.deskRoute1Code(), this.deskRoute1CodeEditable());
      
      // deskRoute2Code
      this.setFormValue('DESK2', item, this.deskRoute2Code(), this.deskRoute2CodeEditable());
      
      // mutual fund fields
      // reinvest
      if (settingCode === 'RCAPG') {
        reinvestCapital = item.settingValue;
        reinvestCapitalEditable = item.allowToEditInd;
      }

      if (settingCode === 'RDINT') {
        reinvestDiv = item.settingValue;
        reinvestDivEditable = item.allowToEditInd;
      }

      // buyAtNav
      this.setFormValue('BYNAV', item, this.buyAtNav(), this.buyAtNavEditable());
      
      this.isEditButtonDisabledForUser = this.isEditButtonDisabledForUser && item.allowToEditInd === 'N';
    });
    if (this.securityType === TradeType.MUTUAL_FUND) {
      this.setFormInitialValuesForMutualFunds(reinvestCapital, reinvestDiv, reinvestCapitalEditable, reinvestDivEditable);
    }
  }

  private setFormInitialValuesForMutualFunds(reinvestCapital: string,reinvestDiv: string, reinvestCapitalEditable: string, reinvestDivEditable: string) {
   
      if (reinvestCapital === 'R' && reinvestDiv === 'R') {
        this.reinvest().setValue('REINVEST');
      }

      if (reinvestCapital === 'R' && reinvestDiv === 'C') {
        this.reinvest().setValue('CAPITAL');
      }

      if (reinvestCapital === 'C' && reinvestDiv === 'R') {
        this.reinvest().setValue('DIVIDEND');
      }

      if (reinvestCapital === 'C' && reinvestDiv === 'C') {
        this.reinvest().setValue('CASH');
      }
      if (this.role === TRADING_SETTINGS_ROLE.ADMIN) {
        this.ibdDefaultValues.set('RCAPG', this.reinvest().value);
      }

      if (reinvestCapitalEditable === 'Y' && reinvestDivEditable === 'Y') {
        this.reinvestEditable().setValue('Y');
      }

      if (reinvestCapitalEditable === 'N' && reinvestDivEditable === 'N') {
        this.reinvestEditable().setValue('N');
      }
   
  }


  addOrdInfAlwaysDisplay(): AbstractControl {
    return this.tradingSettingsForm.get('addOrdInfAlwaysDisplay');
  }

  addOrdInfAlwaysDisplayEditable(): AbstractControl {
    return this.tradingSettingsForm.get('addOrdInfAlwaysDisplayEditable');
  }

  commissionAlwaysDisplay(): AbstractControl {
    return this.tradingSettingsForm.get('commissionAlwaysDisplay');
  }

  commissionAlwaysDisplayEditable(): AbstractControl {
    return this.tradingSettingsForm.get('commissionAlwaysDisplayEditable');
  }

  regInfAlwaysDisplay(): AbstractControl {
    return this.tradingSettingsForm.get('regInfAlwaysDisplay');
  }

  regInfAlwaysDisplayEditable(): AbstractControl {
    return this.tradingSettingsForm.get('regInfAlwaysDisplayEditable');
  }

  accountType(): AbstractControl {
    return this.tradingSettingsForm.get('accountType');
  }

  accountTypeEditable(): AbstractControl {
    return this.tradingSettingsForm.get('accountTypeEditable');
  }

  orderType(): AbstractControl {
    return this.tradingSettingsForm.get('orderType');
  }

  orderTypeEditable(): AbstractControl {
    return this.tradingSettingsForm.get('orderTypeEditable');
  }

  trailingBasedOn(): AbstractControl {
    return this.tradingSettingsForm.get('trailingBasedOn');
  }

  trailingBasedOnEditable(): AbstractControl {
    return this.tradingSettingsForm.get('trailingBasedOnEditable');
  }

  duration(): AbstractControl {
    return this.tradingSettingsForm.get('duration');
  }

  durationEditable(): AbstractControl {
    return this.tradingSettingsForm.get('durationEditable');
  }

  useAveragePrice(): AbstractControl {
    return this.tradingSettingsForm.get('useAveragePrice');
  }

  useAveragePriceEditable(): AbstractControl {
    return this.tradingSettingsForm.get('useAveragePriceEditable');
  }

  solicited(): AbstractControl {
    return this.tradingSettingsForm.get('solicited');
  }

  solicitedEditable(): AbstractControl {
    return this.tradingSettingsForm.get('solicitedEditable');
  }

  discretion(): AbstractControl {
    return this.tradingSettingsForm.get('discretion');
  }

  discretionEditable(): AbstractControl {
    return this.tradingSettingsForm.get('discretionEditable');
  }

  sourceOfInput(): AbstractControl {
    return this.tradingSettingsForm.get('sourceOfInput');
  }

  sourceOfInputEditable(): AbstractControl {
    return this.tradingSettingsForm.get('sourceOfInputEditable');
  }

  commissionType(): AbstractControl {
    return this.tradingSettingsForm.get('commissionType');
  }

  commissionTypeEditable(): AbstractControl {
    return this.tradingSettingsForm.get('commissionTypeEditable');
  }

  ibdMemo(): AbstractControl {
    return this.tradingSettingsForm.get('ibdMemo');
  }

  ibdMemoEditable(): AbstractControl {
    return this.tradingSettingsForm.get('ibdMemoEditable');
  }

  deskRoute1Code(): AbstractControl {
    return this.tradingSettingsForm.get('deskRoute1Code');
  }

  deskRoute1CodeEditable(): AbstractControl {
    return this.tradingSettingsForm.get('deskRoute1CodeEditable');
  }

  deskRoute2Code(): AbstractControl {
    return this.tradingSettingsForm.get('deskRoute2Code');
  }

  deskRoute2CodeEditable(): AbstractControl {
    return this.tradingSettingsForm.get('deskRoute2CodeEditable');
  }

  reinvest(): AbstractControl {
    return this.tradingSettingsForm.get('reinvest');
  }

  reinvestEditable(): AbstractControl {
    return this.tradingSettingsForm.get('reinvestEditable');
  }

  buyAtNav(): AbstractControl {
    return this.tradingSettingsForm.get('buyAtNav');
  }

  buyAtNavEditable(): AbstractControl {
    return this.tradingSettingsForm.get('buyAtNavEditable');
  }

  toggleAdminViewTab(selectedRole: any) {
    if (selectedRole !== this.role) {
      this.tradingSettingsForm.reset();
      this.isEditButtonDisabledForUser = true;
      this.isAdminView = selectedRole === TRADING_SETTINGS_ROLE.ADMIN ? true : false;
      this.role = selectedRole;
      this.getDisplayColumns();
      this.isEditing = false;
      this.fetchTradingSettings();
    }

    this.role = selectedRole;
  }

  private createPayloadFromFormData() {
    let payload: Array<any> = [];
    if (this.isClearDefaultSetting && !this.isAdminView) {
      return [];
    }
    if (this.securityType === TradeType.EQUITY) {
      payload = this.createPayloadFromFormDataForEquity();
    }

    else if (this.securityType === TradeType.MUTUAL_FUND) {
      payload = this.createPayloadFromFormDataForMF();
    }

    else if (this.securityType === TradeType.OPTION) {
      payload = this.createPayloadFromFormDataForOption();
    }
    if(this.isAdminView){
      payload.forEach(element => {
        if(!!element.settingValue && element.allowToEditInd === null ){
          element.allowToEditInd = "Y";
        }
      });
    }
    return payload;

  }

  private createPayloadFromFormDataForMF() {
    let formValue = this.tradingSettingsForm.value;
    let reqData;
    let reinvestCapital: string = null;
    let reinvestDiv: string = null;
    let reinvestCapitalEditable: string = null;
    let reinvestDivEditable: string = null;

    if (formValue.reinvest === 'REINVEST') {
      reinvestCapital = 'R';
      reinvestDiv = 'R';
    }

    if (formValue.reinvest === 'CAPITAL') {
      reinvestCapital = 'R';
      reinvestDiv = 'C';
    }

    if (formValue.reinvest === 'DIVIDEND') {
      reinvestCapital = 'C';
      reinvestDiv = 'R';
    }

    if (formValue.reinvest === 'CASH') {
      reinvestCapital = 'C';
      reinvestDiv = 'C';
    }

    if (formValue.reinvestEditable === 'Y') {
      reinvestCapitalEditable = 'Y';
      reinvestDivEditable = 'Y';
    }

    if (formValue.reinvestEditable === 'N') {
      reinvestCapitalEditable = 'N';
      reinvestDivEditable = 'N';
    }

    if (this.isRiaUser) {
      if (this.isAdmin) {
        reqData = [
          { settingCode: 'RCAPG', settingValue: reinvestCapital, allowToEditInd: reinvestCapitalEditable },
          { settingCode: 'RDINT', settingValue: reinvestDiv, allowToEditInd: reinvestDivEditable },
          { settingCode: 'SOLIC', settingValue: formValue.solicited, allowToEditInd: formValue.solicitedEditable },
          { settingCode: 'DISCR', settingValue: formValue.discretion, allowToEditInd: formValue.discretionEditable }
        ];
      } else {
        reqData = [
          { settingCode: 'RCAPG', settingValue: reinvestCapital },
          { settingCode: 'RDINT', settingValue: reinvestDiv },
          { settingCode: 'SOLIC', settingValue: formValue.solicited, allowToEditInd: formValue.solicitedEditable },
          { settingCode: 'DISCR', settingValue: formValue.discretion, allowToEditInd: formValue.discretionEditable }
        ];
      }
    }

    if (this.isBdUser) {
      this.replaceNullValues(formValue);
      if (this.isAdmin) {
        reqData = [
          { settingCode: 'ADINF', settingValue: formValue.addOrdInfAlwaysDisplay, allowToEditInd: formValue.addOrdInfAlwaysDisplayEditable },
          { settingCode: 'COMMI', settingValue: formValue.commissionAlwaysDisplay, allowToEditInd: formValue.commissionAlwaysDisplayEditable },
          { settingCode: 'REGUL', settingValue: formValue.regInfAlwaysDisplay, allowToEditInd: formValue.regInfAlwaysDisplayEditable },
          { settingCode: 'SOLIC', settingValue: formValue.solicited, allowToEditInd: formValue.solicitedEditable },
          { settingCode: 'RCAPG', settingValue: reinvestCapital, allowToEditInd: reinvestCapitalEditable },
          { settingCode: 'RDINT', settingValue: reinvestDiv, allowToEditInd: reinvestDivEditable },
          { settingCode: 'INPUT', settingValue: formValue.sourceOfInput, allowToEditInd: formValue.sourceOfInputEditable },
          { settingCode: 'DISCR', settingValue: formValue.discretion, allowToEditInd: formValue.discretionEditable },
          { settingCode: 'BYNAV', settingValue: formValue.buyAtNav, allowToEditInd: formValue.buyAtNavEditable }
        ];
      } else {
        reqData = [
          { settingCode: 'ADINF', settingValue: formValue.addOrdInfAlwaysDisplay },
          { settingCode: 'COMMI', settingValue: formValue.commissionAlwaysDisplay },
          { settingCode: 'REGUL', settingValue: formValue.regInfAlwaysDisplay },
          { settingCode: 'SOLIC', settingValue: formValue.solicited },
          { settingCode: 'RCAPG', settingValue: reinvestCapital },
          { settingCode: 'RDINT', settingValue: reinvestDiv },
          { settingCode: 'INPUT', settingValue: formValue.sourceOfInput },
          { settingCode: 'DISCR', settingValue: formValue.discretion },
          { settingCode: 'BYNAV', settingValue: formValue.buyAtNav }
        ];
      }
    }

    return reqData;
  }

  private createPayloadFromFormDataForOption() {
    let formValue = this.tradingSettingsForm.value;
    let reqData;

    if (this.isRiaUser) {
      if (this.isAdmin) {
        reqData = [
          { settingCode: 'ACTYP', settingValue: formValue.accountType, allowToEditInd: formValue.accountTypeEditable },
          { settingCode: 'ORDTY', settingValue: formValue.orderType, allowToEditInd: formValue.orderTypeEditable },
          { settingCode: 'DURTN', settingValue: formValue.duration, allowToEditInd: formValue.durationEditable },
          { settingCode: 'SOLIC', settingValue: formValue.solicited, allowToEditInd: formValue.solicitedEditable },
          { settingCode: 'DISCR', settingValue: formValue.discretion, allowToEditInd: formValue.discretionEditable }
        ];
      } else {
        reqData = [
          { settingCode: 'ACTYP', settingValue: formValue.accountType },
          { settingCode: 'ORDTY', settingValue: formValue.orderType },
          { settingCode: 'DURTN', settingValue: formValue.duration },
          { settingCode: 'SOLIC', settingValue: formValue.solicited, allowToEditInd: formValue.solicitedEditable },
          { settingCode: 'DISCR', settingValue: formValue.discretion, allowToEditInd: formValue.discretionEditable }
        ];
      }
    }

    if (this.isBdUser) {
      this.replaceNullValues(formValue);
      formValue.ibdMemo  = !!formValue.ibdMemo ? 'DIM:'+formValue.ibdMemo : null;
      if (this.isAdmin) {
        reqData = [
          { settingCode: 'ADINF', settingValue: formValue.addOrdInfAlwaysDisplay, allowToEditInd: formValue.addOrdInfAlwaysDisplayEditable },
          { settingCode: 'COMMI', settingValue: formValue.commissionAlwaysDisplay, allowToEditInd: formValue.commissionAlwaysDisplayEditable },
          { settingCode: 'REGUL', settingValue: formValue.regInfAlwaysDisplay, allowToEditInd: formValue.regInfAlwaysDisplayEditable },
          { settingCode: 'ACTYP', settingValue: formValue.accountType, allowToEditInd: formValue.accountTypeEditable },
          { settingCode: 'ORDTY', settingValue: formValue.orderType, allowToEditInd: formValue.orderTypeEditable },
          { settingCode: 'DURTN', settingValue: formValue.duration, allowToEditInd: formValue.durationEditable },
          { settingCode: 'SOLIC', settingValue: formValue.solicited, allowToEditInd: formValue.solicitedEditable },
          { settingCode: 'DISCR', settingValue: formValue.discretion, allowToEditInd: formValue.discretionEditable },
          { settingCode: 'INPUT', settingValue: formValue.sourceOfInput, allowToEditInd: formValue.sourceOfInputEditable },
          { settingCode: 'COMTY', settingValue: formValue.commissionType, allowToEditInd: formValue.commissionTypeEditable },
          { settingCode: 'IBDMO', settingValue: formValue.ibdMemo, allowToEditInd: formValue.ibdMemoEditable }
        ];
      } else {
        reqData = [
          { settingCode: 'ADINF', settingValue: formValue.addOrdInfAlwaysDisplay },
          { settingCode: 'COMMI', settingValue: formValue.commissionAlwaysDisplay },
          { settingCode: 'REGUL', settingValue: formValue.regInfAlwaysDisplay },
          { settingCode: 'ACTYP', settingValue: formValue.accountType },
          { settingCode: 'ORDTY', settingValue: formValue.orderType },
          { settingCode: 'DURTN', settingValue: formValue.duration },
          { settingCode: 'SOLIC', settingValue: formValue.solicited },
          { settingCode: 'DISCR', settingValue: formValue.discretion },
          { settingCode: 'INPUT', settingValue: formValue.sourceOfInput },
          { settingCode: 'COMTY', settingValue: formValue.commissionType },
          { settingCode: 'IBDMO', settingValue: formValue.ibdMemo }
        ];
      }
    }

    return reqData;
  }

  private createPayloadFromFormDataForEquity() {
    let formValue = this.tradingSettingsForm.value;
    let reqData;

    if (this.isRiaUser) {
      // ADINF
      formValue.addOrdInfAlwaysDisplay = formValue.addOrdInfAlwaysDisplay === null ? 'N' : formValue.addOrdInfAlwaysDisplay;
      formValue.addOrdInfAlwaysDisplayEditable = formValue.addOrdInfAlwaysDisplayEditable === null ? 'Y' : formValue.addOrdInfAlwaysDisplayEditable;
      if (this.isAdmin) {
        reqData = [
          { settingCode: 'ADINF', settingValue: formValue.addOrdInfAlwaysDisplay, allowToEditInd: formValue.addOrdInfAlwaysDisplayEditable },
          { settingCode: 'ACTYP', settingValue: formValue.accountType, allowToEditInd: formValue.accountTypeEditable },
          { settingCode: 'ORDTY', settingValue: formValue.orderType, allowToEditInd: formValue.orderTypeEditable },
          { settingCode: 'TBASE', settingValue: formValue.trailingBasedOn, allowToEditInd: formValue.trailingBasedOnEditable },
          { settingCode: 'DURTN', settingValue: formValue.duration, allowToEditInd: formValue.durationEditable },
          { settingCode: 'TUAPR', settingValue: formValue.useAveragePrice, allowToEditInd: formValue.useAveragePriceEditable },
          { settingCode: 'SOLIC', settingValue: formValue.solicited, allowToEditInd: formValue.solicitedEditable },
          { settingCode: 'DISCR', settingValue: formValue.discretion, allowToEditInd: formValue.discretionEditable }
        ];
      } else {
        reqData = [
          { settingCode: 'ADINF', settingValue: formValue.addOrdInfAlwaysDisplay },
          { settingCode: 'ACTYP', settingValue: formValue.accountType },
          { settingCode: 'ORDTY', settingValue: formValue.orderType },
          { settingCode: 'TBASE', settingValue: formValue.trailingBasedOn },
          { settingCode: 'DURTN', settingValue: formValue.duration },
          { settingCode: 'TUAPR', settingValue: formValue.useAveragePrice },
          { settingCode: 'SOLIC', settingValue: formValue.solicited, allowToEditInd: formValue.solicitedEditable },
          { settingCode: 'DISCR', settingValue: formValue.discretion, allowToEditInd: formValue.discretionEditable }
        ];
      }
    }

    if (this.isBdUser) {
      this.replaceNullValues(formValue);
      formValue.ibdMemo  = !!formValue.ibdMemo ? 'ADDL:'+formValue.ibdMemo : null;
      if (this.isAdmin) {
        reqData = [
          { settingCode: 'ADINF', settingValue: formValue.addOrdInfAlwaysDisplay, allowToEditInd: formValue.addOrdInfAlwaysDisplayEditable },
          { settingCode: 'COMMI', settingValue: formValue.commissionAlwaysDisplay, allowToEditInd: formValue.commissionAlwaysDisplayEditable },
          { settingCode: 'REGUL', settingValue: formValue.regInfAlwaysDisplay, allowToEditInd: formValue.regInfAlwaysDisplayEditable },
          { settingCode: 'ACTYP', settingValue: formValue.accountType, allowToEditInd: formValue.accountTypeEditable },
          { settingCode: 'ORDTY', settingValue: formValue.orderType, allowToEditInd: formValue.orderTypeEditable },
          { settingCode: 'TBASE', settingValue: formValue.trailingBasedOn, allowToEditInd: formValue.trailingBasedOnEditable },
          { settingCode: 'DURTN', settingValue: formValue.duration, allowToEditInd: formValue.durationEditable },
          { settingCode: 'SOLIC', settingValue: formValue.solicited, allowToEditInd: formValue.solicitedEditable },
          { settingCode: 'DISCR', settingValue: formValue.discretion, allowToEditInd: formValue.discretionEditable },
          { settingCode: 'INPUT', settingValue: formValue.sourceOfInput, allowToEditInd: formValue.sourceOfInputEditable },
          { settingCode: 'COMTY', settingValue: formValue.commissionType, allowToEditInd: formValue.commissionTypeEditable },
          { settingCode: 'IBDMO', settingValue: formValue.ibdMemo, allowToEditInd: formValue.ibdMemoEditable },
          { settingCode: 'DESK1', settingValue: formValue.deskRoute1Code, allowToEditInd: formValue.deskRoute1CodeEditable },
          { settingCode: 'DESK2', settingValue: formValue.deskRoute2Code, allowToEditInd: formValue.deskRoute2CodeEditable }
        ];
      } else {
        reqData = [
          { settingCode: 'ADINF', settingValue: formValue.addOrdInfAlwaysDisplay },
          { settingCode: 'COMMI', settingValue: formValue.commissionAlwaysDisplay },
          { settingCode: 'REGUL', settingValue: formValue.regInfAlwaysDisplay },
          { settingCode: 'ACTYP', settingValue: formValue.accountType },
          { settingCode: 'ORDTY', settingValue: formValue.orderType },
          { settingCode: 'TBASE', settingValue: formValue.trailingBasedOn },
          { settingCode: 'DURTN', settingValue: formValue.duration },
          { settingCode: 'SOLIC', settingValue: formValue.solicited },
          { settingCode: 'DISCR', settingValue: formValue.discretion },
          { settingCode: 'INPUT', settingValue: formValue.sourceOfInput },
          { settingCode: 'COMTY', settingValue: formValue.commissionType },
          { settingCode: 'IBDMO', settingValue: formValue.ibdMemo },
          { settingCode: 'DESK1', settingValue: formValue.deskRoute1Code },
          { settingCode: 'DESK2', settingValue: formValue.deskRoute2Code }
        ];
      }
    }

    return reqData;
  }

  private replaceNullValues(formValue: any): void {
    // ADINF
    formValue.addOrdInfAlwaysDisplay = formValue.addOrdInfAlwaysDisplay === null ? 'N' : formValue.addOrdInfAlwaysDisplay;
    // COMMI
    formValue.commissionAlwaysDisplay = formValue.commissionAlwaysDisplay === null ? 'N' : formValue.commissionAlwaysDisplay;
    // REGUL
    formValue.regInfAlwaysDisplay = formValue.regInfAlwaysDisplay === null ? 'N' : formValue.regInfAlwaysDisplay;

    // ACTYP
    formValue.accountType = this.checkBlanksValue(formValue.accountType);
    // SOLIC
    formValue.solicited = this.checkBlanksValue(formValue.solicited);
    // COMTY
    formValue.commissionType = this.checkBlanksValue(formValue.commissionType);
    // DISCR
    formValue.discretion = this.checkBlanksValue(formValue.discretion);
    // TUAPR
    formValue.useAveragePrice = this.checkBlanksValue(formValue.useAveragePrice);
    // DESK1
    formValue.deskRoute1Code = this.checkBlanksValue(formValue.deskRoute1Code);
    // DESK2
    formValue.deskRoute2Code = this.checkBlanksValue(formValue.deskRoute2Code);



  }

  private checkBlanksValue(formValueString: string): string {
    return formValueString === null ? 'BLANK' : formValueString;
  }

  // This function is called when loading the data for adminView, if there is any allowToEditInd = null
  // the data will be saved with  allowToEditInd = "Y"
  private adminLoadReplaceNullAllowToEditInd(tradeDefaultSettingList: Array<any>): boolean {
    let isAnyNull:boolean = false;
    tradeDefaultSettingList.forEach(element => {
      if(!!element.settingValue && element.allowToEditInd === null ){
        element.allowToEditInd = "Y";
        isAnyNull = true;
      }
    });

    if(isAnyNull){
      let tradingType = TRADING_SETTINGS_ASSET_TYPE.EQT;
      if (this.securityType === TradeType.MUTUAL_FUND) {
        tradingType = TRADING_SETTINGS_ASSET_TYPE.MFT;
      }
      else if (this.securityType === TradeType.OPTION) {
        tradingType = TRADING_SETTINGS_ASSET_TYPE.OPT;
      }
      this.tradingService.saveTradingSettings(
        this.platform,
        this.account.ibdId,
        tradingType,
        this.role,
        tradeDefaultSettingList
      ).subscribe(data => {
        this.fetchTradingSettings();
      });
    }
    console.log('results: '+ tradeDefaultSettingList);
    return isAnyNull;
  }



  public saveOrderSettings(): void {
    this.orderSettingsSaveInProgress = true;
    const payload = this.createPayloadFromFormData();
    if (this.isAdminView || this.isDifferentArray(payload, this.settingsIbdResp)) {
      let tradingType = TRADING_SETTINGS_ASSET_TYPE.EQT;
      if (this.securityType === TradeType.MUTUAL_FUND) {
        tradingType = TRADING_SETTINGS_ASSET_TYPE.MFT;
      }
      else if (this.securityType === TradeType.OPTION) {
        tradingType = TRADING_SETTINGS_ASSET_TYPE.OPT;
      }
      this.tradingService.saveTradingSettings(
        this.platform,
        this.account.ibdId,
        tradingType,
        this.role,
        payload
      ).subscribe(data => {
        this.showSavedConfirmation = true;
        this.fetchTradingSettings();
      });
    } else {
      this.orderSettingsSaveInProgress = false;
      this.openAlertMessageDialog();
    }
  }
  //payload.length === 0 :case for userView to "Clear User Defaults"
  isDifferentArray(payload: Array<any>, ibdDefaults: Array<any>): boolean {
    if (!!payload && payload.length === 0) {
      return true;
    }
    if (ibdDefaults?.length === 0) {
      return true;
    }
    //result has values present in payload but different or missing in ibdDefaults
    let results = payload.filter(({ settingCode: sc1, settingValue: sv1 }) =>
      !ibdDefaults.some(({ settingCode: sc2, settingValue: sv2 }) =>
        ( sc2 === sc1 && sv1 === sv2)));

    if (results.length > 0) {
      //(sc2 === sc1 && sv1===null) to validate  where settingValue change to null
      // !!sv1 to validate where settingValue change different than null
      let results2 = results.filter(({ settingCode: sc1, settingValue: sv1 }) =>
       ibdDefaults.some(({ settingCode: sc2, settingValue: sv2 }) =>
        ( (sc2 === sc1 && sv1===null) || !!sv1)));
      if( results2.length > 0) return true;
    }
    // payload and ibdDefaults are the same
    return false;

  }

  startEditing() {
    if (this.isUserDefaultEmpty) {
      // set the default values from Admin View
      this.setFormInitialValues(this.settingsIbdResp);
    }
    this.isEditing = true;
    this.orderSettingsSaveInProgress = false;
    this.showSavedConfirmation = false;
    this.showErrorMessage = false;
  }

  cancelEditing() {
    this.isEditing = false;
    this.setFormInitialValues(this.settingsResp);
  }

  closePopup() {
    this.role = null;
    this.setSecurityType();
    this.fetchTradingSettings();
    if (this.dialogRef) {
      this.dialogRef.close();
    } else {
      // TODO
      console.log('Navigate back to Originator');
    }
  }

  openAlertMessageDialog() {
    const alertMessageDialogRef = this.dialog.open(AlertMessageDialogComponent, {
        panelClass: 'modal',
        maxWidth: '45%',
        disableClose: true,
        autoFocus: false,
        data: {
            msgHeaderLabel: '',
            msgBodyLabel: 'messages.NO_CHANGES_IN_ORDER_SETTINGS',
            msgLevel: ALERT_MESSAGE_LEVEL.warn,
            confirmButtonLabel: 'labels.ok',
            activeTheme:'comfortable'
        }
    });

    alertMessageDialogRef.afterClosed().subscribe((data) => {
        console.log('proceeding : ', data);
        
    });
}
}
